import type { StatusTone } from "@/components/ui";
import type {
  BusinessStatus,
  CapabilityStatus,
  VerificationStatus,
  VisibilityStatus,
} from "./types";

export function businessStatusTone(status: BusinessStatus): StatusTone {
  switch (status) {
    case "active":
    case "verified":
      return "success";
    case "draft":
    case "applied":
      return "info";
    case "under_review":
    case "more_information_required":
    case "restricted":
      return "warning";
    case "suspended":
    case "rejected":
      return "danger";
    case "closed":
      return "neutral";
    default:
      return "neutral";
  }
}

export function visibilityStatusTone(status: VisibilityStatus): StatusTone {
  switch (status) {
    case "visible":
      return "success";
    case "paused":
    case "hidden":
      return "warning";
    case "restricted":
      return "danger";
    default:
      return "neutral";
  }
}

export function verificationStatusTone(status: VerificationStatus): StatusTone {
  switch (status) {
    case "verified":
    case "accepted":
      return "success";
    case "requested":
    case "submitted":
    case "under_review":
    case "more_information_required":
      return "warning";
    case "rejected":
    case "revoked":
    case "expired":
      return "danger";
    default:
      return "neutral";
  }
}

export function capabilityStatusTone(status: CapabilityStatus): StatusTone {
  switch (status) {
    case "active":
    case "enabled":
      return "success";
    case "pending_setup":
    case "paused":
      return "warning";
    case "restricted":
      return "danger";
    case "retired":
    case "not_applicable":
      return "neutral";
    default:
      return "neutral";
  }
}

/**
 * Names a reviewer would use, where the enum value is not one.
 *
 * `request_more_information` is what the lifecycle calls it; "Modify" is what a
 * reviewer is actually doing — handing the application back to be corrected.
 * The wire value never changes, only what is read on screen.
 */
const ACTION_LABELS: Record<string, string> = {
  request_more_information: "Modify",
  mark_verified: "Mark verified",
  start_review: "Start review",
  // Membership formats (i259: Subscription is the fourth).
  session_pack: "Session pack",
  period_pass: "Period pass",
  member_rate: "Member rate",
  subscription: "Subscription",
};

export function formatLabel(value: string): string {
  return ACTION_LABELS[value] ?? value.replaceAll("_", " ");
}


/**
 * The documents a business type asks for, read from what the backend serves.
 *
 * `onboarding_requirements.sections` comes from the canonical list in
 * `domain/businesses/onboarding-requirements.ts`. Reading it rather than
 * keeping a copy here is the point: a reviewer must tick exactly the documents
 * the applicant is shown, and two hand-maintained lists would not stay equal.
 */
export interface RequirementDocument {
  key: string;
  label: string;
  required: boolean;
  /** Depends on an answer — DTI or SEC, never both. */
  conditional: boolean;
  /** The answer it depends on, as served: `business_structure` ∈ [...]. */
  showWhen?: { key: string; equals: string[] };
}

/**
 * Does this document apply to THIS applicant, given their answers?
 *
 *   true   it applies (unconditional, or the answer selects it)
 *   false  the answer rules it out (a corporation is not asked for DTI)
 *   null   it depends on an answer the applicant has not given
 */
export function documentApplies(
  doc: RequirementDocument,
  answers: Record<string, unknown> | null | undefined,
): boolean | null {
  if (!doc.showWhen) return true;
  const raw = answers?.[doc.showWhen.key];
  if (raw === undefined || raw === null || String(raw).trim() === "") return null;
  return doc.showWhen.equals.includes(String(raw));
}

/**
 * Required documents that are not accepted yet, as labels a reviewer reads —
 * with the conditional DTI/SEC one resolved from the applicant's business
 * structure. A conditional document whose answer is missing is named with
 * that caveat rather than dropped: "not accepted" must not read as "not owed".
 */
export function outstandingDocumentLabels(
  requirements: RequirementDocument[],
  acceptedKeys: ReadonlySet<string>,
  answers: Record<string, unknown> | null | undefined,
): string[] {
  const out: string[] = [];
  for (const r of requirements) {
    if (!r.required || acceptedKeys.has(r.key)) continue;
    const applies = documentApplies(r, answers);
    if (applies === false) continue;
    out.push(
      applies === null && r.showWhen
        ? `${r.label} (if it applies — ${formatLabel(r.showWhen.key).toLowerCase()} not answered)`
        : r.label,
    );
  }
  return out;
}

interface ServedField {
  key?: unknown;
  label?: unknown;
  kind?: unknown;
  required?: unknown;
  showWhen?: unknown;
}

function isShowWhen(v: unknown): v is { key: string; equals: string[] } {
  if (!v || typeof v !== "object") return false;
  const o = v as { key?: unknown; equals?: unknown };
  return (
    typeof o.key === "string" &&
    Array.isArray(o.equals) &&
    o.equals.every((e) => typeof e === "string")
  );
}

export function documentsFromRequirements(type: {
  onboarding_requirements?: Record<string, unknown> | null;
}): RequirementDocument[] {
  const sections = type.onboarding_requirements?.["sections"];
  if (!Array.isArray(sections)) return [];
  const out: RequirementDocument[] = [];
  for (const section of sections) {
    const fields = (section as { fields?: unknown })?.fields;
    if (!Array.isArray(fields)) continue;
    for (const raw of fields as ServedField[]) {
      if (raw?.kind !== "file" || typeof raw.key !== "string") continue;
      out.push({
        key: raw.key,
        label: typeof raw.label === "string" ? raw.label : raw.key,
        required: raw.required === true,
        conditional: Boolean(raw.showWhen),
        ...(isShowWhen(raw.showWhen) ? { showWhen: raw.showWhen } : {}),
      });
    }
  }
  return out;
}


/**
 * Turn a requirement key into something a reviewer can read.
 *
 * Most title-case cleanly; the exceptions are acronyms and proper nouns, where
 * a generic transform produces "Tin" and "Bir 2303".
 */
const ANSWER_LABELS: Record<string, string> = {
  business_email: "Business email",
  business_phone: "Business phone",
  address: "Physical address",
  map_pin: "Google Maps link",
  business_structure: "Business structure",
  tin: "TIN",
  bir_2303: "BIR 2303",
  base_city: "Base city / region",
  willing_to_travel: "Willing to travel",
  years_experience: "Years of experience",
  certifications: "Certifications",
  experience: "Experience",
  sports: "Primary sports",
  business_name: "Business name",
  legal_name: "Legal name",
  display_name: "Display name",
  about: "About",
};

export function answerLabel(key: string): string {
  return (
    ANSWER_LABELS[key] ??
    key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  );
}
